'use client'

import { useState, useMemo, useEffect } from 'react'
import { User, Property, Application, Payment } from '@/types'
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { commissionApi } from '@/lib/api'


interface PlatformAnalyticsProps {
  users: User[]
  properties: Property[]
  applications: Application[]
  payments?: Payment[]
}

export default function PlatformAnalytics({ users, properties, applications, payments = [] }: PlatformAnalyticsProps) {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d')
  const [commissionRate, setCommissionRate] = useState<number>(0.10) // Default 10%, loaded from API

  // Load commission rate
  useEffect(() => {
    const loadCommissionRate = async () => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
        if (!token) return
        
        const response = await commissionApi.getRate(token)
        if (response.success && response.data) {
          setCommissionRate((response.data as any).commissionRate || 0.10)
        }
      } catch (error) {
        console.error('Error loading commission rate:', error)
      }
    }
    
    loadCommissionRate()
  }, [])

  // Calculate analytics based on time range
  const getAnalytics = () => {
    const now = new Date()
    const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000))

    const recentUsers = users.filter(user => new Date(user.created_at) >= cutoffDate)
    const recentProperties = properties.filter(property => new Date(property.created_at) >= cutoffDate)
    const recentApplications = applications.filter(app => new Date(app.created_at) >= cutoffDate)

    // Compute payment-derived revenue within time window
    const recentPayments = payments.filter(p => new Date(p.created_at) >= cutoffDate && p.status === 'completed')
    const totalRevenue = recentPayments.reduce((sum, p) => sum + p.amount, 0)
    const commission = recentPayments.reduce((sum, p) => sum + (p.commission_amount || 0), 0)

    return {
      newUsers: recentUsers.length,
      newProperties: recentProperties.length,
      newApplications: recentApplications.length,
      acceptedApplications: recentApplications.filter(app => app.status === 'accepted').length,
      totalRevenue,
      commission
    }
  }

  const analytics = getAnalytics()

  // Calculate growth rates
  const getGrowthRate = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return Math.round(((current - previous) / previous) * 100)
  }

  // Mock previous period data (in real app, you'd fetch this from database)
  const previousAnalytics = {
    newUsers: Math.floor(analytics.newUsers * 0.8),
    newProperties: Math.floor(analytics.newProperties * 0.9),
    newApplications: Math.floor(analytics.newApplications * 0.85)
  }

  // Generate chart data for the selected time range
  const generateChartData = useMemo(() => {
    const now = new Date()
    const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
    const data: Array<{ date: string; users: number; properties: number; applications: number }> = []

    // Create data points for each day in the range
    for (let i = daysAgo - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000))
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      
      // Count items created on or before this date (cumulative)
      const cutoffDate = new Date(date.getTime() + (24 * 60 * 60 * 1000)) // End of day
      
      const usersCount = users.filter(u => new Date(u.created_at) <= cutoffDate).length
      const propertiesCount = properties.filter(p => new Date(p.created_at) <= cutoffDate).length
      const applicationsCount = applications.filter(a => new Date(a.created_at) <= cutoffDate).length

      data.push({
        date: dateStr,
        users: usersCount,
        properties: propertiesCount,
        applications: applicationsCount
      })
    }

    return data
  }, [users, properties, applications, timeRange])

  // Calculate application rate data (applications per property)
  const applicationRateData = useMemo(() => {
    const now = new Date()
    const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
    const data: Array<{ date: string; rate: number }> = []

    for (let i = daysAgo - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000))
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      
      // Get properties and applications up to this date
      const cutoffDate = new Date(date.getTime() + (24 * 60 * 60 * 1000))
      const propertiesUpToDate = properties.filter(p => new Date(p.created_at) <= cutoffDate)
      const applicationsUpToDate = applications.filter(a => new Date(a.created_at) <= cutoffDate)
      
      // Calculate rate: applications per property (or 0 if no properties)
      const rate = propertiesUpToDate.length > 0 
        ? Math.round((applicationsUpToDate.length / propertiesUpToDate.length) * 100) / 100
        : 0

      data.push({
        date: dateStr,
        rate: rate
      })
    }

    return data
  }, [properties, applications, timeRange])

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Platform Analytics</h2>
        <div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">New Users</h3>
              <p className="text-2xl font-bold text-blue-600">{analytics.newUsers}</p>
            </div>
            <div className="text-right">
              <span className={`text-sm font-medium ${
                getGrowthRate(analytics.newUsers, previousAnalytics.newUsers) >= 0 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                {getGrowthRate(analytics.newUsers, previousAnalytics.newUsers)}%
              </span>
              <p className="text-xs text-gray-500">vs previous</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">New Properties</h3>
              <p className="text-2xl font-bold text-green-600">{analytics.newProperties}</p>
            </div>
            <div className="text-right">
              <span className={`text-sm font-medium ${
                getGrowthRate(analytics.newProperties, previousAnalytics.newProperties) >= 0 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                {getGrowthRate(analytics.newProperties, previousAnalytics.newProperties)}%
              </span>
              <p className="text-xs text-gray-500">vs previous</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">New Applications</h3>
              <p className="text-2xl font-bold text-purple-600">{analytics.newApplications}</p>
            </div>
            <div className="text-right">
              <span className={`text-sm font-medium ${
                getGrowthRate(analytics.newApplications, previousAnalytics.newApplications) >= 0 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                {getGrowthRate(analytics.newApplications, previousAnalytics.newApplications)}%
              </span>
              <p className="text-xs text-gray-500">vs previous</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Accepted Applications</h3>
              <p className="text-2xl font-bold text-orange-600">{analytics.acceptedApplications}</p>
            </div>
            <div className="text-right">
              <span className="text-sm font-medium text-green-600">
                {analytics.acceptedApplications > 0 ? Math.round((analytics.acceptedApplications / analytics.newApplications) * 100) : 0}%
              </span>
              <p className="text-xs text-gray-500">acceptance rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Overview</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Revenue</span>
              <span className="text-2xl font-bold text-green-600">₦{analytics.totalRevenue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Platform Commission</span>
              <span className="text-xl font-semibold text-blue-600">₦{analytics.commission.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Commission Rate</span>
              <span className="text-sm text-gray-500">{(commissionRate * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">User Distribution</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Landlords</span>
              <span className="text-xl font-semibold text-blue-600">
                {users.filter(user => user.role === 'landlord').length}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Clients</span>
              <span className="text-xl font-semibold text-green-600">
                {users.filter(user => user.role === 'client').length}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Verified Users</span>
              <span className="text-xl font-semibold text-purple-600">
                {users.filter(user => user.is_verified).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Activity Trends</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* User Growth Chart */}
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-500 mb-2">User Growth</h4>
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateChartData}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorUsers)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Property Listings Chart */}
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Property Listings</h4>
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateChartData}>
                  <defs>
                    <linearGradient id="colorProperties" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="properties" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorProperties)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Application Rate Chart */}
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Application Rate</h4>
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={applicationRateData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                    formatter={(value: number | undefined) => value !== undefined ? `${value.toFixed(2)} per property` : '0 per property'}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="rate" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {applications.slice(0, 5).map((application) => (
            <div key={application.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  New application for property
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(application.created_at).toLocaleDateString()}
                </p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {application.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
} 


